// =============================================================================
// PlanProgress — /plans — staff-only, read-only monitoring view of active
// payment-plan enrollments (Front Office queue #4)
// S45 — new screen consuming GET /api/enrollments/plans (no mutations)
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { supabase } from '../lib/supabase.js';
import { api } from '../lib/api.js';
import NotificationBell from '../components/NotificationBell.jsx';

const PAGE_SIZE = 50;

// Reuses the exact hues RecruitList.jsx already assigns to the analogous
// recruit-pipeline statuses, rather than inventing new palette values.
const PLAN_STATUS_COLORS = {
  active:    'bg-green-100 text-green-800',
  past_due:  'bg-amber-100 text-amber-800',
  completed: 'bg-hunter-deep text-white',
  canceled:  'bg-slate-200 text-slate-600',
};

// Single UTC-anchored date formatter (PF-2.1): the /plans date fields
// (plan_next_charge_date, start_date, enrollment_end) are all YYYY-MM-DD date
// strings. Rendering them without timeZone:'UTC' shifts the calendar date one
// day early for every non-UTC staff viewer -- formatDate used to omit the
// guard while its formatShortDate sibling carried it. Folded into one base so
// the guard can't drift out of one of them again; each column keeps its own
// display style via the opts. The length===10 branch handles the bare date
// strings; the else is defensive if a full timestamp ever arrives.
function formatDateUTC(iso, opts) {
  if (!iso) return '—';
  const d = String(iso).length === 10 ? new Date(`${iso}T00:00:00Z`) : new Date(iso);
  return d.toLocaleDateString('en-US', { ...opts, timeZone: 'UTC' });
}
const LONG_DATE = { month: 'short', day: 'numeric', year: 'numeric' };   // "Aug 3, 2026"
const SHORT_DATE = { month: '2-digit', day: '2-digit', year: '2-digit' }; // "08/03/26"

const formatDate = (iso) => formatDateUTC(iso, LONG_DATE);
// MM/DD/YY -- used only for the Enrollment (term) column per spec.
const formatShortDate = (iso) => formatDateUTC(iso, SHORT_DATE);

function formatWholeDollars(n) {
  return Math.round(Number(n || 0)).toLocaleString();
}

export default function PlanProgress() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [plans, setPlans] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/auth/me').then(setMe).catch(() => {});
  }, []);

  const fetchPlans = useCallback(async (off) => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.get(`/api/enrollments/plans?limit=${PAGE_SIZE}&offset=${off}`);
      setPlans(data.plans);
      setTotal(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans(offset);
  }, [offset, fetchPlans]);

  async function signOut() {
    await supabase.auth.signOut();
    navigate('/login', { replace: true });
  }

  const startIndex = total === 0 ? 0 : offset + 1;
  const endIndex = Math.min(offset + PAGE_SIZE, total);

  return (
    <div className="min-h-screen bg-silver-a">

      {/* Header */}
      <header className="bg-hunter-deep border-b border-hunter">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <div>
            <div className="font-display font-black text-white text-2xl leading-none">CCF Front Office</div>
            <div className="text-mid-green text-[0.65rem] tracking-[0.25em] uppercase font-bold mt-1">
              Coach Cole Football
            </div>
          </div>
          <div className="flex items-center gap-5 text-sm">
            <NotificationBell />
            {me && (
              <span className="text-silver-a">
                Welcome, <span className="font-bold text-white">{me.team.full_name}</span>
              </span>
            )}
            <Link to="/" className="text-silver-a hover:text-white underline">← Front Office</Link>
            <button onClick={signOut} className="text-silver-a hover:text-white underline">
              Sign Out
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-8">
        <div className="mb-6">
          <h1 className="font-display font-black text-2xl text-silver-text">Payment Plans</h1>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded p-4 mb-4">
            {error}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="text-sm text-silver-mute py-12 text-center">Loading…</div>
        ) : plans.length === 0 ? (
          <div className="text-sm text-silver-mute py-12 text-center">No active payment plans yet.</div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-[0.65rem] font-bold uppercase tracking-wide text-silver-mute">
                    <th className="px-4 py-3">Recruit</th>
                    <th className="px-4 py-3">Status</th>
                    <th className="px-4 py-3">RC</th>
                    <th className="px-4 py-3">Progress</th>
                    <th className="px-4 py-3">Paid</th>
                    <th className="px-4 py-3">Installment</th>
                    <th className="px-4 py-3">Next charge</th>
                    <th className="px-4 py-3">Enrollment Term</th>
                  </tr>
                </thead>
                <tbody>
                  {plans.map(p => (
                    <tr key={p.enrollment_id} className="border-b border-gray-100 last:border-0">
                      <td className="px-4 py-3 font-medium text-silver-text">
                        {p.recruit_name || <span className="italic text-silver-mute">— (deleted)</span>}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-[0.6rem] font-bold uppercase tracking-wide px-2 py-0.5 rounded ${PLAN_STATUS_COLORS[p.plan_status] || 'bg-gray-100 text-silver-text'}`}>
                          {p.plan_status?.replace(/_/g, ' ')}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-silver-text">
                        {p.rc_name || '—'}
                      </td>
                      <td className="px-4 py-3 text-silver-text">
                        {p.installments_paid} / {p.installment_count}
                      </td>
                      <td className="px-4 py-3 text-silver-text">
                        ${formatWholeDollars(p.total_paid)} / ${formatWholeDollars(p.plan_total)}
                      </td>
                      <td className="px-4 py-3 text-silver-text">
                        ${formatWholeDollars(p.installment_amount)}/mo
                      </td>
                      <td className="px-4 py-3 text-silver-text">
                        {/* PF-2.2: terminal plans never carry a live next charge; render — regardless
                            of the stored value (belt-and-suspenders over PF-1.5, and covers rows
                            whose dates were written before that server fix). */}
                        {['completed', 'canceled'].includes(p.plan_status) ? '—' : formatDate(p.next_charge_date)}
                      </td>
                      <td className="px-4 py-3 text-silver-text">
                        {formatShortDate(p.start_date)} / {p.enrollment_end === 'career' ? 'Career' : formatShortDate(p.enrollment_end)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination — simple Prev/Next, no search/filter to coordinate with */}
            <div className="mt-6 flex flex-col items-center gap-3">
              {total > 0 && (
                <p className="text-sm text-silver-mute">
                  Showing {startIndex}–{endIndex} of {total}
                </p>
              )}
              {total > PAGE_SIZE && (
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setOffset(o => Math.max(0, o - PAGE_SIZE))}
                    disabled={offset === 0}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Prev
                  </button>
                  <button
                    onClick={() => setOffset(o => (o + PAGE_SIZE < total ? o + PAGE_SIZE : o))}
                    disabled={offset + PAGE_SIZE >= total}
                    className="px-3 py-1.5 text-sm rounded border border-gray-300 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </div>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
